"use client";

import {
  type KeyboardEvent,
  type PropsWithChildren,
  type ReactNode,
  useEffect,
  useState,
  type FC
} from "react";
import { XIcon, PlusIcon, FileText } from "lucide-react";
import {
  AttachmentPrimitive,
  ComposerPrimitive,
  MessagePrimitive,
  useAuiState,
  useAui
} from "@assistant-ui/react";
import { useShallow } from "zustand/shallow";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/agents-v2/components/ui/tooltip";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogTrigger
} from "@/agents-v2/components/ui/dialog";
import { ScrollArea } from "@/agents-v2/components/ui/scroll-area";
import { Avatar, AvatarImage, AvatarFallback } from "@/agents-v2/components/ui/avatar";
import { TooltipIconButton } from "@/agents-v2/components/assistant-ui/tooltip-icon-button";
import { getMimeTypeIcon } from "@/agents-v2/components/assistant-ui/file";
import { cn } from "@/agents-v2/utils/cn";

const useFileSrc = (file: File | undefined) => {
  const [src, setSrc] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!file) {
      setSrc(undefined);
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    setSrc(objectUrl);

    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [file]);

  return src;
};

const useAttachmentSrc = () => {
  const { file, src } = useAuiState(
    useShallow((s): { file?: File; src?: string } => {
      if (s.attachment.type !== "image") return {};
      if (s.attachment.file) return { file: s.attachment.file };
      const src = s.attachment.content?.filter((c) => c.type === "image")[0]
        ?.image;
      if (!src) return {};
      return { src };
    })
  );

  return useFileSrc(file) ?? src;
};

const useAttachmentMeta = () => {
  return useAuiState(
    useShallow((s) => {
      const attachment = s.attachment as {
        name?: string;
        contentType?: string;
        file?: File;
        type?: string;
      };

      return {
        contentType:
          attachment.contentType ??
          attachment.file?.type ??
          "application/octet-stream",
        name: attachment.name ?? attachment.file?.name ?? "Attachment",
        size: attachment.file?.size,
        type: attachment.type
      };
    })
  );
};

const getExtension = (name: string) => {
  const extension = name.split(".").pop();
  return extension && extension !== name ? extension.toUpperCase() : null;
};

const getFileTypeLabel = (name: string, contentType: string) => {
  const lowerName = name.toLowerCase();
  const extension = getExtension(name);

  if (
    contentType === "application/pdf" ||
    contentType === "application/msword" ||
    contentType ===
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
    lowerName.endsWith(".pdf") ||
    lowerName.endsWith(".doc") ||
    lowerName.endsWith(".docx")
  ) {
    return "Document";
  }

  if (
    contentType === "text/csv" ||
    contentType === "application/vnd.ms-excel" ||
    contentType ===
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
    lowerName.endsWith(".csv") ||
    lowerName.endsWith(".xls") ||
    lowerName.endsWith(".xlsx")
  ) {
    return "Spreadsheet";
  }

  if (
    contentType === "application/vnd.ms-powerpoint" ||
    contentType ===
      "application/vnd.openxmlformats-officedocument.presentationml.presentation" ||
    lowerName.endsWith(".ppt") ||
    lowerName.endsWith(".pptx")
  ) {
    return "Presentation";
  }

  if (contentType.startsWith("image/")) return "Image";
  if (contentType.startsWith("text/")) return "Text File";
  if (contentType.startsWith("audio/")) return "Audio File";
  if (contentType.startsWith("video/")) return "Video File";

  return extension ? `${extension} File` : "File";
};

type AttachmentPreviewProps = {
  src: string;
};

const AttachmentPreview: FC<AttachmentPreviewProps> = ({ src }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  return (
    <img
      src={src}
      alt="Attachment preview"
      className={cn(
        "block h-auto max-h-[80vh] w-auto max-w-full object-contain",
        isLoaded
          ? "aui-attachment-preview-image-loaded"
          : "aui-attachment-preview-image-loading invisible"
      )}
      onLoad={() => setIsLoaded(true)}
    />
  );
};

const AttachmentPreviewDialog: FC<PropsWithChildren<{ src?: string }>> = ({
  children,
  src
}) => {
  if (!src) return children;

  return (
    <Dialog>
      <DialogTrigger
        className="aui-attachment-preview-trigger cursor-pointer transition-colors hover:bg-accent/50"
        asChild
      >
        {children}
      </DialogTrigger>
      <DialogContent className="aui-attachment-preview-dialog-content p-2 sm:max-w-3xl [&>button]:rounded-full [&>button]:bg-foreground/60 [&>button]:p-1 [&>button]:opacity-100 [&>button]:ring-0! [&_svg]:text-background [&>button]:hover:[&_svg]:text-destructive">
        <DialogTitle className="aui-sr-only sr-only">
          Image Attachment Preview
        </DialogTitle>
        <div className="aui-attachment-preview relative mx-auto flex max-h-[80dvh] w-full items-center justify-center overflow-hidden bg-background">
          <AttachmentPreview src={src} />
        </div>
      </DialogContent>
    </Dialog>
  );
};

const AttachmentThumb: FC<{ src?: string }> = ({ src }) => {
  return (
    <Avatar className="aui-attachment-tile-avatar h-full w-full rounded-none">
      <AvatarImage
        src={src}
        alt="Attachment preview"
        className="aui-attachment-tile-image object-cover"
      />
      <AvatarFallback>
        <FileText className="aui-attachment-tile-fallback-icon size-8 text-muted-foreground" />
      </AvatarFallback>
    </Avatar>
  );
};

const AttachmentUI: FC = () => {
  const aui = useAui();
  const isComposer = aui.attachment.source !== "message";

  if (isComposer) return <ComposerAttachmentUI />;

  return <MessageAttachmentUI />;
};

const MessageAttachmentUI: FC = () => {
  const src = useAttachmentSrc();
  const hasPreview = Boolean(src);

  const isImage = useAuiState((s) => s.attachment.type === "image");
  const typeLabel = useAuiState((s) => {
    const type = s.attachment.type;
    switch (type) {
      case "image":
        return "Image";
      case "document":
        return "Document";
      case "file":
        return "File";
      default:
        return type;
    }
  });
  const handlePreviewKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!hasPreview) return;
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    event.currentTarget.click();
  };

  return (
    <Tooltip>
      <AttachmentPrimitive.Root
        className={cn(
          "aui-attachment-root relative",
          isImage && "aui-attachment-root-composer only:*:first:size-24"
        )}
      >
        <AttachmentPreviewDialog src={src}>
          <TooltipTrigger asChild>
            <div
              className={cn(
                "aui-attachment-tile size-14 overflow-hidden rounded-[calc(var(--composer-radius)-var(--composer-padding))] border bg-muted transition-opacity",
                hasPreview && "cursor-pointer hover:opacity-75"
              )}
              role={hasPreview ? "button" : undefined}
              tabIndex={hasPreview ? 0 : undefined}
              aria-label={hasPreview ? `${typeLabel} attachment` : undefined}
              onKeyDown={hasPreview ? handlePreviewKeyDown : undefined}
            >
              <AttachmentThumb src={src} />
            </div>
          </TooltipTrigger>
        </AttachmentPreviewDialog>
      </AttachmentPrimitive.Root>
      <TooltipContent side="top">
        <AttachmentPrimitive.Name />
      </TooltipContent>
    </Tooltip>
  );
};

const ComposerAttachmentUI: FC = () => {
  const src = useAttachmentSrc();
  const { contentType, name } = useAttachmentMeta();
  const Icon = getMimeTypeIcon(contentType);
  const fileTypeLabel = getFileTypeLabel(name, contentType);

  const leading = src ? (
    <AttachmentPreviewDialog src={src}>
      <button
        type="button"
        className="flex size-8 shrink-0 items-center justify-center overflow-hidden rounded border border-border bg-background"
        aria-label={`Preview ${name}`}
      >
        <img
          src={src}
          alt=""
          className="h-full w-full object-cover"
          aria-hidden="true"
        />
      </button>
    </AttachmentPreviewDialog>
  ) : (
    <span className="flex size-8 shrink-0 items-center justify-center rounded border border-border bg-background text-muted-foreground">
      <Icon className="size-4" aria-hidden="true" />
    </span>
  );

  return (
    <AttachmentPrimitive.Root className="aui-attachment-root group relative shrink-0">
      <div className="aui-composer-attachment-chip inline-flex w-64 max-w-[70vw] items-center gap-2 rounded-md border border-border bg-muted/50 px-2.5 py-1.5 text-sm">
        {leading}
        <div className="min-w-0 flex-1">
          <div className="truncate font-medium leading-5" title={name}>
            {name}
          </div>
          <div className="text-xs leading-4 text-muted-foreground">
            {fileTypeLabel}
          </div>
        </div>
        <AttachmentPrimitive.Remove asChild>
          <TooltipIconButton
            tooltip={`Remove ${name}`}
            side="top"
            className="size-6 shrink-0 rounded-sm text-muted-foreground opacity-70 hover:bg-muted-foreground/15 hover:text-destructive group-hover:opacity-100 focus-visible:opacity-100"
            aria-label={`Remove ${name}`}
          >
            <XIcon className="size-4" aria-hidden="true" />
          </TooltipIconButton>
        </AttachmentPrimitive.Remove>
      </div>
    </AttachmentPrimitive.Root>
  );
};

export const UserMessageAttachments: FC = () => {
  return (
    <div className="aui-user-message-attachments-end col-span-full col-start-1 row-start-1 flex w-full flex-row justify-end gap-2">
      <MessagePrimitive.Attachments>
        {() => <AttachmentUI />}
      </MessagePrimitive.Attachments>
    </div>
  );
};

export const ComposerAttachments: FC<{ leading?: ReactNode }> = ({
  leading
}) => {
  const attachmentCount = useAuiState((s) => s.composer.attachments.length);

  if (attachmentCount === 0 && !leading) return null;

  return (
    <ScrollArea
      type="hover"
      scrollHideDelay={300}
      scrollBarOrientation="horizontal"
      viewportClassName="h-auto w-full"
      className="aui-composer-attachments w-full pb-2"
    >
      <div className="flex w-max min-w-full flex-row items-center gap-2 py-1 pr-2">
        {leading}
        <ComposerPrimitive.Attachments>
          {() => <AttachmentUI />}
        </ComposerPrimitive.Attachments>
      </div>
    </ScrollArea>
  );
};

export const ComposerAddAttachment: FC = () => {
  return (
    <ComposerPrimitive.AddAttachment asChild>
      <TooltipIconButton
        tooltip="Add Attachment"
        side="bottom"
        variant="ghost"
        size="icon"
        className="aui-composer-add-attachment size-8 rounded-full p-1 font-semibold text-xs hover:bg-muted-foreground/15 dark:border-muted-foreground/15 dark:hover:bg-muted-foreground/30"
        aria-label="Add Attachment"
      >
        <PlusIcon className="aui-attachment-add-icon size-5 stroke-[1.5px]" />
      </TooltipIconButton>
    </ComposerPrimitive.AddAttachment>
  );
};
